/**
 * useAlbums — 앨범 CRUD 훅
 */

import { useCallback } from 'react'
import { useApiFetch, useApiMutation } from './useApiFetch.js'
import type { AlbumImage, AlbumGroup, SnapType } from '@cugumi/shared/types'

export type { AlbumImage, AlbumGroup } from '@cugumi/shared/types'

export interface AlbumGeneration {
  id: string
  generatedUrls: string[] | null
  style: string | null
  role: string | null
  createdAt: string
}

export interface Album {
  id: string
  name: string
  snapType: SnapType
  status: 'draft' | 'completed' | 'applied'
  images: AlbumImage[]
  groups: AlbumGroup[]
  generations?: AlbumGeneration[]
  createdAt: string
  updatedAt: string
}

interface AlbumsResponse {
  albums: Album[]
}

const QUERY_KEY = '/api/ai/albums'

/**
 * 앨범 목록 조회 + 생성
 */
export function useAlbums() {
  const { data, error, isLoading, refetch } = useApiFetch<AlbumsResponse>(QUERY_KEY)

  const createMutation = useApiMutation<Album, { name: string; snapType: string }>(
    QUERY_KEY,
    'POST',
    { invalidateKeys: [[QUERY_KEY]] },
  )

  return {
    albums: data?.albums ?? [],
    error,
    isLoading,
    refetch,
    createAlbum: createMutation.trigger,
    isCreating: createMutation.isLoading,
  }
}

/**
 * 단일 앨범 상세 조회
 */
export function useAlbum(id: string | null) {
  const path = id ? `${QUERY_KEY}/${id}` : null
  const { data, error, isLoading, refetch } = useApiFetch<{ album: Album }>(path)

  const updateMutation = useApiMutation<Album, Partial<Pick<Album, 'name' | 'groups' | 'images'>>>(
    `${QUERY_KEY}/${id}`,
    'PUT',
    { invalidateKeys: [[QUERY_KEY], ...(id ? [[`${QUERY_KEY}/${id}`]] : [])] },
  )

  const deleteMutation = useApiMutation<void>(
    `${QUERY_KEY}/${id}`,
    'DELETE',
    { invalidateKeys: [[QUERY_KEY]] },
  )

  const updateAlbum = useCallback(
    async (body: Partial<Pick<Album, 'name' | 'groups' | 'images'>>) => {
      if (!id) return null
      return updateMutation.trigger(body)
    },
    [id, updateMutation.trigger],
  )

  const deleteAlbum = useCallback(
    async () => {
      if (!id) return null
      return deleteMutation.trigger()
    },
    [id, deleteMutation.trigger],
  )

  return {
    album: data?.album ?? null,
    error,
    isLoading,
    refetch,
    updateAlbum,
    isUpdating: updateMutation.isLoading,
    deleteAlbum,
    isDeleting: deleteMutation.isLoading,
  }
}
